import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search, Check, ChevronDown, X, CheckSquare, Square } from "lucide-react";

/**
 * Dialogue de recherche/sélection avec fenêtre de taille fixe.
 * - multiple=false : sélection unique. multiple=true : sélection multiple.
 * - secteurs + getSecteurs : ajoute un filtre par secteur.
 * - en mode multiple : bouton « Tout sélectionner / Tout désélectionner ».
 */
export function SearchSelectDialog({ open, onClose, title, items = [], getLabel, getSub, onSelect, multiple = false, initial, testid, secteurs, getSecteurs }) {
  const [q, setQ] = useState("");
  const [secFilter, setSecFilter] = useState("");
  const [sel, setSel] = useState(multiple ? (initial || []) : (initial || ""));
  useEffect(() => { if (open) { setQ(""); setSecFilter(""); setSel(multiple ? (initial || []) : (initial || "")); } /* eslint-disable-next-line */ }, [open]);

  const filtered = items.filter((i) => {
    const matchText = `${getLabel(i)} ${getSub ? getSub(i) || "" : ""}`.toLowerCase().includes(q.toLowerCase());
    const matchSec = !secFilter || (getSecteurs && (getSecteurs(i) || []).includes(secFilter));
    return matchText && matchSec;
  });
  const isSel = (id) => (multiple ? sel.includes(id) : sel === id);
  const toggle = (id) => { if (multiple) setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); else setSel(id); };
  const canValidate = multiple ? sel.length > 0 : !!sel;

  const allSelected = multiple && filtered.length > 0 && filtered.every((i) => sel.includes(i.id));
  const toggleAll = () => {
    const ids = filtered.map((i) => i.id);
    if (allSelected) setSel(sel.filter((x) => !ids.includes(x)));
    else setSel(Array.from(new Set([...sel, ...ids])));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">Recherchez puis sélectionnez un ou plusieurs éléments dans la liste.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <input autoFocus data-testid={testid ? `${testid}-search` : undefined} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un contact…" className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          {secteurs && secteurs.length > 0 && (
            <select data-testid={testid ? `${testid}-secteur-filter` : undefined} value={secFilter} onChange={(e) => setSecFilter(e.target.value)} className="px-2 py-2 rounded-lg border border-slate-300 text-sm max-w-[130px]">
              <option value="">Tous secteurs</option>
              {secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
        </div>

        {multiple && (
          <button type="button" data-testid={testid ? `${testid}-toggle-all` : undefined} onClick={toggleAll}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-900 self-start">
            {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
          </button>
        )}

        <div className="h-72 overflow-auto mt-2 space-y-1 border border-slate-100 rounded-lg p-1 bg-slate-50/40">
          {filtered.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Aucun contact.</div>}
          {filtered.map((i) => (
            <button key={i.id} type="button" data-testid={testid ? `${testid}-opt-${i.id}` : undefined} onClick={() => toggle(i.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between ${isSel(i.id) ? "bg-emerald-50 border border-emerald-300" : "bg-white hover:bg-slate-50 border border-transparent"}`}>
              <span><span className="font-medium text-slate-900">{getLabel(i)}</span>{getSub && getSub(i) ? <span className="text-slate-400 ml-2">{getSub(i)}</span> : null}</span>
              {isSel(i.id) && <Check className="w-4 h-4 text-emerald-600" />}
            </button>
          ))}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          <button type="button" data-testid={testid ? `${testid}-confirm` : undefined} disabled={!canValidate} onClick={() => onSelect(sel)}
            className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium disabled:opacity-50">Valider{multiple && sel.length ? ` (${sel.length})` : ""}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Champ de sélection unique (affiche la valeur, ouvre le dialogue au clic). */
export function SearchSelectField({ label, placeholder = "Sélectionner…", dialogTitle, items = [], value, getLabel, getSub, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);
  return (
    <div>
      {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
      <button type="button" data-testid={testid} onClick={() => setOpen(true)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-left flex items-center justify-between text-sm bg-white hover:border-slate-400">
        <span className={selected ? "text-slate-900" : "text-slate-400"}>{selected ? getLabel(selected) : placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>
      <SearchSelectDialog open={open} onClose={() => setOpen(false)} title={dialogTitle || label} items={items}
        getLabel={getLabel} getSub={getSub} initial={value} testid={testid}
        onSelect={(id) => { onChange(id); setOpen(false); }} />
    </div>
  );
}

/** Champ de sélection multiple avec puces (chips) et dialogue de recherche. */
export function MultiSelectField({ label, placeholder = "Ajouter…", dialogTitle, items = [], value = [], getLabel, getSub, onChange, testid, secteurs, getSecteurs }) {
  const [open, setOpen] = useState(false);
  const selected = items.filter((i) => value.includes(i.id));
  return (
    <div>
      {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
      <div className="mt-1 flex flex-wrap gap-1.5 items-center border border-slate-300 rounded-lg px-2 py-2 min-h-[42px] bg-white">
        {selected.length === 0 && <span className="text-slate-400 text-sm">{placeholder}</span>}
        {selected.map((i) => (
          <span key={i.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            {getLabel(i)}
            <button type="button" onClick={() => onChange(value.filter((v) => v !== i.id))} className="hover:text-emerald-900"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <button type="button" data-testid={testid} onClick={() => setOpen(true)} className="ml-auto text-emerald-600 text-xs font-medium">+ Modifier</button>
      </div>
      <SearchSelectDialog open={open} onClose={() => setOpen(false)} title={dialogTitle || label} multiple items={items}
        getLabel={getLabel} getSub={getSub} initial={value} testid={testid} secteurs={secteurs} getSecteurs={getSecteurs}
        onSelect={(ids) => { onChange(ids); setOpen(false); }} />
    </div>
  );
}
